import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useGACheckout } from '../GACheckoutProvider'
import { useGAModals } from '../GAModalsProvider'
import {
  formatEventSchedule,
  timezones,
  capitalizeString,
  formatAddress,
} from '../../../../utilities/helpers'
import authService from '../../../../utilities/services/auth.service'
import {
  resolveBrandLogo,
  resolvePrimaryColor,
  resolveButtonColor,
  resolveButtonTextColor,
} from '../../utils/branding'

import { GetTickets } from '../../components/GetStatus/GetTickets'
import { GetSoldout } from '../../components/GetStatus/GetSoldout'
import { Countdown } from '../../components/Countdown'
import { LoadingContainer } from '../../../LoadingContainer'

import placeholder from '../../../../assets/placeholder.png'

import './gaPurchasePage.scss'

const TRUST_ITEMS = [
  {
    t: 'Mobile tickets.',
    d: ' Securely stored in your account.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="5" y="2" width="14" height="20" rx="3" />
        <line x1="10" y1="18.5" x2="14" y2="18.5" />
      </svg>
    ),
  },
  {
    t: 'Buyer protection.',
    d: ' Safe from bots and scalpers.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  },
  {
    t: 'Prices are all-in.',
    d: ' Taxes and fees included.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-7.2-7.2a2 2 0 0 1-.6-1.4V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.8z" />
        <line x1="7.5" y1="7.5" x2="7.51" y2="7.5" />
      </svg>
    ),
  },
]

export default function GAPurchaseContainer({
  selectedFromQuickpicks,
  event,
  isScheduled,
  scheduledTime,
  quickpicks,
  isSoldout,
  selectQuickPick,
  isLoading,
}) {
  const { openCBModal } = useGAModals()
  const { checkout } = useGACheckout()
  const navigate = useNavigate()
  const loggedIn = authService.isLoggedIn()

  const [lowestPrice, setLowestPrice] = useState(null)

  useEffect(() => {
    if (selectedFromQuickpicks) {
      checkout()
    }
  }, [selectedFromQuickpicks])

  useEffect(() => {
    if (quickpicks && quickpicks.length > 0) {
      const quickpickPrices = quickpicks
        .filter((quickpick) => quickpick && quickpick.price != null)
        .map((quickpick) => quickpick.price)

      const discountPrices = quickpicks
        .filter((quickpick) => quickpick?.offer?.connected_offers?.length > 0)
        .flatMap((quickpick) =>
          quickpick.offer.connected_offers.flatMap((offer) =>
            offer?.am_pricing_objects?.map((pricing) => pricing?.totalDue)
          )
        )

      const validPrices = [
        ...quickpickPrices,
        ...(discountPrices?.length > 0 ? discountPrices : []),
      ]

      if (validPrices.length > 0) {
        setLowestPrice(Math.min(...validPrices))
      } else {
        setLowestPrice(null)
      }
    } else {
      setLowestPrice(null)
    }
  }, [quickpicks])

  const organization = event?.organization
  const accent = resolvePrimaryColor(event, organization)
  const buttonColor = resolveButtonColor(event, organization, accent)
  const buttonTextColor = resolveButtonTextColor(event, organization)
  const logoSrc = resolveBrandLogo(event, organization)
  const timezone = timezones(event?.venue?.timezone)
  const when = formatEventSchedule(event, timezone)
  const venueLine = [
    event?.venue?.name,
    event?.venue?.address?.[0]
      ? `${capitalizeString(event.venue.address[0].city)}, ${capitalizeString(
          event.venue.address[0].state
        )}`
      : null,
  ]
    .filter(Boolean)
    .join(', ')

  const providerLabel = organization?.name
    ? `Official ticketing marketplace for ${organization.name} venues`
    : 'Official ticketing marketplace'

  const imageSrc = event?.image?.url || organization?.image?.url || null
  const attractions = Array.isArray(event?.attractions) ? event.attractions : []

  return (
    <div
      className="ga-purchase-page"
      style={{
        '--spp-accent': accent,
        '--spp-button': buttonColor,
        '--spp-button-text': buttonTextColor,
      }}
    >
      <header className="ga-header">
        <div className="ga-header__inner">
          <Link to="/" className="ga-header__brand">
            {logoSrc ? (
              <img
                className="ga-header__logo"
                src={logoSrc}
                alt={organization?.name || 'Organization'}
              />
            ) : (
              <span className="ga-header__org">
                {organization?.name || 'Blocktickets'}
              </span>
            )}
          </Link>
          <div className="ga-header__actions">
            <form
              className="ga-search"
              onSubmit={(e) => {
                e.preventDefault()
                const q = new FormData(e.currentTarget).get('q')
                if (q) {
                  navigate(`/search?query=${encodeURIComponent(String(q))}`)
                }
              }}
            >
              <input
                name="q"
                placeholder="Search events, artists, or venues"
                aria-label="Search events, artists, or venues"
              />
              <button type="submit" aria-label="Search">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </form>
            {loggedIn ? (
              <Link to="/my-events" className="ga-login-btn">
                My wallet
              </Link>
            ) : (
              <Link to="/login" className="ga-login-btn">
                Login
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="ga-main">
        <aside className="ga-aside">
          <div className="ga-card ga-poster">
            <div className="ga-poster__frame">
              {imageSrc ? (
                <img
                  className="ga-poster__img"
                  src={imageSrc}
                  alt={event?.name || 'Event'}
                />
              ) : null}
            </div>
          </div>
          <div className="ga-card ga-trust">
            {TRUST_ITEMS.map((item) => (
              <div key={item.t} className="ga-trust__row">
                {item.icon}
                <span>
                  <strong>{item.t}</strong>
                  {item.d}
                </span>
              </div>
            ))}
          </div>
        </aside>

        <div className="ga-content">
          <section className="ga-card ga-event-meta">
            <h1 className="ga-event-meta__title">{event?.name}</h1>
            {venueLine ? (
              <p className="ga-event-meta__venue">{venueLine}</p>
            ) : null}
            {when ? <p className="ga-event-meta__when">{when}</p> : null}
          </section>

          <ContentRenderer
            isScheduled={isScheduled}
            scheduledTime={scheduledTime}
            isSoldout={isSoldout}
            event={event}
            isLoading={isLoading}
            quickpicks={quickpicks}
            selectQuickPick={selectQuickPick}
            openCBModal={openCBModal}
            lowestPrice={lowestPrice}
          />

          {event?.summary ? (
            <section className="ga-card ga-section">
              <p className="ga-section__eyebrow">About this event</p>
              <p className="ga-section__body">{event.summary}</p>
            </section>
          ) : null}

          {attractions.length > 0 ? (
            <section className="ga-card ga-section">
              <p className="ga-section__eyebrow">Who’s playing</p>
              <ul className="ga-lineup">
                {[...attractions]
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((attraction, index) => (
                    <li
                      key={attraction.id || index}
                      className="ga-lineup__item"
                    >
                      <img
                        src={attraction?.artwork?.url || placeholder}
                        alt={attraction.name}
                      />
                      <span>{attraction.name}</span>
                    </li>
                  ))}
              </ul>
            </section>
          ) : null}

          {event?.venue ? (
            <section className="ga-card ga-section">
              <p className="ga-section__eyebrow">Venue</p>
              <div className="ga-venue">
                <img
                  className="ga-venue__img"
                  src={event?.venue?.image?.[0]?.url || placeholder}
                  alt={event?.venue?.name || 'Venue'}
                />
                <div className="ga-venue__info">
                  <h3 className="ga-venue__name">{event.venue.name}</h3>
                  <p className="ga-venue__address">
                    {formatAddress(event.venue)}
                  </p>
                  {event?.venue?.address?.[0] ? (
                    <a
                      className="ga-venue__directions"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        [
                          event.venue.address[0].address_1,
                          event.venue.address[0].city,
                          event.venue.address[0].state,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Directions
                    </a>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </main>

      <div className="ga-market-footer" role="contentinfo">
        <div className="ga-market-footer__inner">
          <span className="ga-market-footer__brand">blocktickets</span>
          <span className="ga-market-footer__divider" aria-hidden />
          <span className="ga-market-footer__label">{providerLabel}</span>
        </div>
      </div>
    </div>
  )
}

function ContentRenderer({
  isScheduled,
  scheduledTime,
  isSoldout,
  event,
  quickpicks,
  selectQuickPick,
  isLoading,
  lowestPrice,
  openCBModal,
}) {
  const timezone = timezones(event?.venue?.timezone)
  const hasOffers = Array.isArray(quickpicks) && quickpicks.length > 0

  if (isLoading) {
    return (
      <section className="ga-card ga-tickets">
        <LoadingContainer />
      </section>
    )
  }

  if (isSoldout) {
    return (
      <section className="ga-card ga-tickets">
        <h2 className="ga-tickets__title">Get tickets</h2>
        <GetSoldout fixedMobile={false} event={event} />
      </section>
    )
  }

  if (isScheduled && scheduledTime && !hasOffers) {
    return (
      <section className="ga-card ga-tickets">
        <h2 className="ga-tickets__title">Get tickets</h2>
        <Countdown scheduledTime={scheduledTime} timezone={timezone} />
      </section>
    )
  }

  return (
    <section className="ga-card ga-tickets">
      <h2 className="ga-tickets__title">Get tickets</h2>
      <GetTickets
        event={event}
        quickpicks={quickpicks}
        selectQuickPick={selectQuickPick}
        openCBModal={openCBModal}
        lowestPrice={lowestPrice}
        showTitle={false}
        asCard={false}
        isScheduled={isScheduled}
        scheduledTime={scheduledTime}
      />
    </section>
  )
}
